import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { Button } from '../../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import {
  Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogFooter,
} from '../../components/ui/dialog';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Plus, Pencil, Trash2, ToggleLeft, ToggleRight, Loader2 } from 'lucide-react';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const initialState = {
  name: '',
  city: '',
  address: '',
  rating: 3,
  price_per_night: 0,
  distance_to_haram: 0,
  total_rooms: 0,
  facilities: '',
};

const HotelsManagement = () => {
  const [hotels, setHotels] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editing, setEditing] = useState(null);
  const [formData, setFormData] = useState(initialState);
  const [saving, setSaving] = useState(false);

  const fetchHotels = useCallback(async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API}/hotels/?show_all=true`);
      setHotels(response.data);
    } catch (error) {
      if (process.env.NODE_ENV === 'development') console.error('Error fetching hotels:', error);
      toast.error('Gagal memuat data hotel');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchHotels(); }, [fetchHotels]);

  const handleOpen = (hotel = null) => {
    if (hotel) {
      setEditing(hotel);
      setFormData({
        name: hotel.name || '',
        city: hotel.city || '',
        address: hotel.address || '',
        rating: hotel.rating || 3,
        price_per_night: hotel.price_per_night || 0,
        distance_to_haram: hotel.distance_to_haram || 0,
        total_rooms: hotel.total_rooms || 0,
        facilities: Array.isArray(hotel.facilities) ? hotel.facilities.join(', ') : '',
      });
    } else {
      setEditing(null);
      setFormData(initialState);
    }
    setShowModal(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const payload = {
        ...formData,
        rating: parseInt(formData.rating),
        price_per_night: parseFloat(formData.price_per_night),
        distance_to_haram: parseFloat(formData.distance_to_haram),
        total_rooms: parseInt(formData.total_rooms),
        facilities: formData.facilities.split(',').map(f => f.trim()).filter(Boolean),
      };
      if (editing) {
        await axios.put(`${API}/hotels/${editing.id}`, payload);
        toast.success('Hotel berhasil diupdate');
      } else {
        await axios.post(`${API}/hotels/`, payload);
        toast.success('Hotel berhasil ditambahkan');
      }
      setShowModal(false);
      fetchHotels();
    } catch (err) {
      toast.error(editing ? 'Gagal update hotel' : 'Gagal menambah hotel');
    } finally {
      setSaving(false);
    }
  };

  const handleToggleActive = async (hotelId, currentStatus) => {
    try {
      await axios.put(`${API}/hotels/${hotelId}`, { is_active: !currentStatus });
      toast.success('Status berhasil diubah');
      fetchHotels();
    } catch (error) {
      toast.error('Gagal mengubah status');
    }
  };

  const handleDelete = async (hotelId) => {
    if (!window.confirm('Yakin ingin menghapus hotel ini?')) return;
    try {
      await axios.delete(`${API}/hotels/${hotelId}`);
      toast.success('Hotel berhasil dihapus');
      fetchHotels();
    } catch {
      toast.error('Gagal menghapus hotel');
    }
  };

  if (loading) {
    return <div className="text-center py-8">Memuat data...</div>;
  }

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <h1 className="text-3xl font-bold font-['Outfit']">Hotels Management</h1>
        <Button className="rounded-full" onClick={() => handleOpen()}>
          <Plus className="h-4 w-4 mr-2" /> Tambah Hotel
        </Button>
      </div>

      <Card className="rounded-2xl">
        <CardHeader><CardTitle>Daftar Hotel</CardTitle></CardHeader>
        <CardContent>
          {hotels.length === 0 ? (
            <p className="text-center text-muted-foreground py-8">Belum ada data hotel</p>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b">
                    <th className="text-left p-4 font-semibold">Nama Hotel</th>
                    <th className="text-left p-4 font-semibold">Lokasi</th>
                    <th className="text-left p-4 font-semibold">Rating</th>
                    <th className="text-left p-4 font-semibold">Harga</th>
                    <th className="text-left p-4 font-semibold">Jarak Haram</th>
                    <th className="text-left p-4 font-semibold">Status</th>
                    <th className="text-right p-4 font-semibold">Aksi</th>
                  </tr>
                </thead>
                <tbody>
                  {hotels.map((hotel) => (
                    <tr key={hotel.id} className="border-b hover:bg-muted/50">
                      <td className="p-4 font-medium">{hotel.name}</td>
                      <td className="p-4 text-muted-foreground">{hotel.city}</td>
                      <td className="p-4"><Badge variant="secondary">{hotel.rating} ⭐</Badge></td>
                      <td className="p-4">Rp {hotel.price_per_night?.toLocaleString('id-ID')}</td>
                      <td className="p-4 text-muted-foreground">{hotel.distance_to_haram} m</td>
                      <td className="p-4">
                        <Badge variant={hotel.is_active ? 'default' : 'secondary'}>
                          {hotel.is_active ? 'Aktif' : 'Nonaktif'}
                        </Badge>
                      </td>
                      <td className="p-4 text-right">
                        <div className="flex justify-end gap-2">
                          <Button variant="outline" size="sm" onClick={() => handleToggleActive(hotel.id, hotel.is_active)}>
                            {hotel.is_active ? <ToggleRight className="h-4 w-4" /> : <ToggleLeft className="h-4 w-4" />}
                          </Button>
                          <Button variant="outline" size="sm" onClick={() => handleOpen(hotel)}>
                            <Pencil className="h-4 w-4" />
                          </Button>
                          <Button variant="destructive" size="sm" onClick={() => handleDelete(hotel.id)}>
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>

      <Dialog open={showModal} onOpenChange={setShowModal}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>{editing ? 'Edit Hotel' : 'Tambah Hotel Baru'}</DialogTitle>
            <DialogDescription>Lengkapi form untuk {editing ? 'mengupdate' : 'menambahkan'} hotel</DialogDescription>
          </DialogHeader>
          <form onSubmit={handleSubmit}>
            <div className="grid gap-4 py-4">
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="name">Nama Hotel *</Label>
                  <Input id="name" value={formData.name} onChange={(e) => setFormData({...formData, name: e.target.value})} required />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="city">Kota *</Label>
                  <Input id="city" value={formData.city} onChange={(e) => setFormData({...formData, city: e.target.value})} required />
                </div>
              </div>
              <div className="space-y-2">
                <Label htmlFor="address">Alamat</Label>
                <Input id="address" value={formData.address} onChange={(e) => setFormData({...formData, address: e.target.value})} />
              </div>
              <div className="grid grid-cols-3 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="rating">Bintang *</Label>
                  <Input id="rating" type="number" min="1" max="5" value={formData.rating} onChange={(e) => setFormData({...formData, rating: e.target.value})} required />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="price_per_night">Harga/Malam *</Label>
                  <Input id="price_per_night" type="number" value={formData.price_per_night} onChange={(e) => setFormData({...formData, price_per_night: e.target.value})} required />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="distance_to_haram">Jarak Haram (m)</Label>
                  <Input id="distance_to_haram" type="number" value={formData.distance_to_haram} onChange={(e) => setFormData({...formData, distance_to_haram: e.target.value})} />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="total_rooms">Jumlah Kamar</Label>
                  <Input id="total_rooms" type="number" value={formData.total_rooms} onChange={(e) => setFormData({...formData, total_rooms: e.target.value})} />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="facilities">Fasilitas (pisah koma)</Label>
                  <Input id="facilities" value={formData.facilities} onChange={(e) => setFormData({...formData, facilities: e.target.value})} placeholder="WiFi, AC, TV" />
                </div>
              </div>
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setShowModal(false)}>Batal</Button>
              <Button type="submit" disabled={saving}>
                {saving ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
                {editing ? 'Update' : 'Tambah'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default HotelsManagement;
